import { Box, Grid, Typography } from '@mui/material';
import { type ReactElement, useState } from 'react';

import type { QuoteStatus } from '@core/types/api/QuoteStatus/QuoteStatus';
import { SlabButton } from '@web/components/SlabButton/SlabButton';
import { SlabDrawerLayout } from '@web/components/SlabDrawer/SlabDrawerLayout';

type QuoteDrawerApprovedPageProps = {
  onClose: () => Promise<void> | void;
  onAutoApprove: () => Promise<void> | void;
  onApproveAndSend: () => Promise<void> | void;
  onSend: () => void;
  isSubmitPending: boolean;
  currentQuoteStatus: QuoteStatus;
};

export const QuoteDrawerApprovedPage = ({
  currentQuoteStatus,
  onClose,
  onAutoApprove,
  onApproveAndSend,
  onSend,
  isSubmitPending,
}: QuoteDrawerApprovedPageProps): ReactElement => {
  const [actionTaken, setActionTaken] = useState<'saveAsApproved' | 'sendQuote'>();
  return (
    <SlabDrawerLayout>
      {currentQuoteStatus.isApprovedToSend() ? (
        <>
          <Box>
            <Typography variant='h1' fontSize='2rem'>
              Quote Saved
            </Typography>
            <p>
              Your quote complies with all approval policies and has been approved. If you’re done
              editing the quote, you can click &quot;Send&quot; to email it to the customer.
            </p>
          </Box>
          <Grid container spacing={2} sx={{ '& button': { width: '100%' } }}>
            <Grid item xs={6}>
              <SlabButton variant='slab-secondary' onClick={onClose} disabled={isSubmitPending}>
                Close
              </SlabButton>
            </Grid>
            <Grid item xs={6}>
              <SlabButton
                variant='slab-primary'
                onClick={() => {
                  setActionTaken('sendQuote');
                  onSend();
                }}
                disabled={isSubmitPending}
                loading={isSubmitPending && actionTaken === 'sendQuote'}
              >
                Send
              </SlabButton>
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          <Box>
            <Typography variant='h1' fontSize='2rem'>
              Quote Saved
            </Typography>
            <p>
              Your quote complies with all approval policies. If you’re done editing the quote, you
              can click &quot;Auto Approve & Send&quot; to email it to the customer.
            </p>
          </Box>
          <Grid container spacing={2} sx={{ '& button': { width: '100%' } }}>
            <Grid item xs={4}>
              <SlabButton variant='slab-secondary' onClick={onClose} disabled={isSubmitPending}>
                Save as Draft
              </SlabButton>
            </Grid>
            <Grid item xs={4}>
              <SlabButton
                variant='slab-secondary'
                onClick={() => {
                  setActionTaken('saveAsApproved');
                  onAutoApprove();
                }}
                disabled={isSubmitPending}
                loading={isSubmitPending && actionTaken === 'saveAsApproved'}
              >
                Auto Approve
              </SlabButton>
            </Grid>
            <Grid item xs={4}>
              <SlabButton
                variant='slab-primary'
                onClick={() => {
                  setActionTaken('sendQuote');
                  onApproveAndSend();
                }}
                disabled={isSubmitPending}
                loading={isSubmitPending && actionTaken === 'sendQuote'}
              >
                Auto Approve & Send
              </SlabButton>
            </Grid>
          </Grid>
        </>
      )}
    </SlabDrawerLayout>
  );
};
